import { CartState, PRODUCTS, ProductType, RegisterMode, KorpoData } from 'cart-model';
import { ProcessMetadata } from 'common-event';
import { loadStateFromStorage, saveStateInStorage } from 'common-react';
import { Fund, Risk } from 'funds-model';
import _ from 'lodash';
import { combineReducers } from 'redux';
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Screen from '../components/ProductWizard/Screen';
import ApiState from './ApiState';
import Config from './Config';
import FundFilter, { emptyFilter } from './FundFilter';
import ProcessType from './ProcessType';
import ProductWizardState from './ProductWizardState';
import UiState from './UiState';

export interface CartPayload {
  readonly fund: Fund;
  readonly productType: ProductType;
}

export interface AddCardPayload extends CartPayload {
  readonly screenCode: string;
}

export interface CartAmountPayload extends CartPayload {
  readonly amount: number;
}

const configInitialState: Config = {
  dataWizardEnabled: true,
  testAutomationEnabled: false,
};

const configSlice = createSlice({
  name: 'config',
  initialState: configInitialState,
  reducers: {
    setConfig: (state: Config, action: PayloadAction<Config>) => action.payload,
  },
});

const uiStateInitialState: UiState = {
  processType: ProcessType.generic,
  initialFilter: emptyFilter(),
  progress: 0,
  risks: [],
  monthlyAmount: 0,
  initialAmount: 0,
  isPensionProcess: false,
};

const uiStateSlice = createSlice({
  name: 'uiState',
  initialState: uiStateInitialState,
  reducers: {
    setProcessType: (state: UiState, action: PayloadAction<ProcessType>) => {return {...state, processType: action.payload};},
    setInitialFilter: (state: UiState, action: PayloadAction<FundFilter>) => {return {...state, initialFilter: action.payload};},
    setProgress: (state: UiState, action: PayloadAction<number>) => {return {...state, progress: action.payload};},
    setProductType: (state: UiState, action: PayloadAction<ProductType>) => {return {
      ...state,
      initialAmount: PRODUCTS[action.payload] ? PRODUCTS[action.payload].initialAmount : 0,
      productType: action.payload};},
    setInitialAmount: (state: UiState, action: PayloadAction<number>) => {return {...state, initialAmount: action.payload};},
    setMonthlyAmount: (state: UiState, action: PayloadAction<number>) => {return {...state, monthlyAmount: action.payload};},
    addRisk: (state: UiState, action: PayloadAction<Risk>) => {return {...state, risks: _.concat(state.risks, action.payload)};},
    removeRisk: (state: UiState, action: PayloadAction<Risk>) => {return {...state, risks: _.filter(state.risks, r => r.id !== action.payload.id)};},
    setIsPensionProcess: (state: UiState, action: PayloadAction<boolean>) => ({...state, isPensionProcess: action.payload}),
  },
});

const apiInitialState: ApiState = {
  funds: [],
  fundTypes: [],
  risks: [],
  horizons: [],
  umbrellas: [],
  currentLimits: { year: 0, currentLimits: { IKE: {limit: 0, monthlyLimit: 0}, IKZE: {limit: 0, monthlyLimit: 0} } },
  blockedFundIdns: [],
  unitCategories: [],
};

const apiStateSlice = createSlice({
  name: 'apiState',
  initialState: apiInitialState,
  reducers: {
    setApiState: (state: ApiState, action: PayloadAction<ApiState>) => action.payload,
    setFundPhoto: (state: ApiState, action: PayloadAction<{ fundPhoto: string, fundId: number, fundPhotoType?: string }>) =>
    ({
      ...state,
      funds: state.funds.map(f => f.id === action.payload.fundId
        ? { ...f, fundPhoto: action.payload.fundPhoto, isApiFundPhotoChecked: true, fundPhotoType: action.payload.fundPhotoType }
        : f)
    }),
  },
});

const cartInitialState: CartState = {
  cartFunds: [],
  screenWhereUserAddFunds: undefined,
  isEntrepreneur: undefined,
  entrepreneurNip: undefined,
  registerMode: RegisterMode.OPEN,
  korpoData: undefined,
  transferInstitutionName: undefined,
};

export const cartStateSlice = createSlice({
  name: 'cartState',
  initialState: cartInitialState,
  reducers: {
    addCartFund: (state: CartState, action: PayloadAction<AddCardPayload>) => {
      return {
        ...state,
        cartFunds: _.concat(state.cartFunds,
            {
              fund: action.payload.fund,
              productType: action.payload.productType,
              amount: PRODUCTS[action.payload.productType].initialAmount,
            }),
        screenWhereUserAddFunds: state.screenWhereUserAddFunds || getScreenName(action.payload.screenCode),
      };
    },
    addTransferCartFund: (state: CartState, action: PayloadAction<AddCardPayload>) => {
      return {
        ...state,
        cartFunds: [{
          fund: action.payload.fund,
          productType: action.payload.productType,
          amount: 0,
        }],
        screenWhereUserAddFunds: state.screenWhereUserAddFunds || getScreenName(action.payload.screenCode),
      };
    },
    addCartFunds: (state: CartState, action: PayloadAction<CartAmountPayload[]>) => {
      const fundsToAdd = action.payload;
      // Filtering existing funds in order to avoid duplicates
      const newFunds = _.filter(state.cartFunds, cf => !fundsToAdd.find(f => f.fund.id === cf.fund.id && f.productType === cf.productType));
      return {
        ...state,
        cartFunds: _.concat(newFunds, fundsToAdd),
      };
    },
    removeCartFund: (state: CartState, action: PayloadAction<CartPayload>) => {
      return {...state, cartFunds: _.filter(state.cartFunds, cf => cf.fund.id !== action.payload.fund.id || action.payload.productType !== cf.productType)};
    },
    retainFundsByProductType: (state: CartState, action: PayloadAction<ProductType>) => {
      return {...state, cartFunds: _.filter(state.cartFunds, cf => action.payload === cf.productType)};
    },
    setCartFundAmount: (state: CartState, action: PayloadAction<CartAmountPayload>) => {
      return {...state, cartFunds: state.cartFunds.map(
            cf => cf.fund.id !== action.payload.fund.id || action.payload.productType !== cf.productType
                ? cf
                : action.payload )};
    },
    setIsEntrepreneur: (state: CartState, action: PayloadAction<boolean | undefined>) => {
      return {...state, isEntrepreneur: action.payload};
    },
    setEntrepreneurNip: (state: CartState, action: PayloadAction<string | undefined>) => {
      return {...state, entrepreneurNip: action.payload};
    },
    setRegisterMode: (state: CartState, action: PayloadAction<RegisterMode>) => {
      return {...state, registerMode: action.payload};
    },
    setKorpoData: (state: CartState, action: PayloadAction<KorpoData | undefined>) => {
      return {...state, korpoData: action.payload};
    },
    setTransferInstitutionName: (state: CartState, action: PayloadAction<string | undefined>) => {
      return {...state, transferInstitutionName: action.payload};
    },
    clearCart: () => {
      return { cartFunds: [], registerMode: RegisterMode.OPEN };
    },
    clearCartFunds:(state: CartState) => {
      return { cartFunds: [], isEntrepreneur: state.isEntrepreneur, entrepreneurNip: state.entrepreneurNip, registerMode: RegisterMode.OPEN};
    },
  },
});

export interface ProcessMetadataProductTypePayload {
  readonly selectedTypeOfInvestment: ProductType;
}

function getScreenName(sourceScreenCode: string) {
  const SCREENS: Map<string, string> = new Map([
    [Screen.SELECT_FUNDS.code, 'expert list'],
    [Screen.SELECT_RECOMMENDED_FUNDS.code, 'preference list'],
    [Screen.SET_AMOUNTS_PER_FUND.code, 'checkout page'],
  ]);
  return SCREENS.get(sourceScreenCode);
}

const processMetadataInitialState: ProcessMetadata = {
  selectedTypeOfInvestment: undefined,
};

const chosenPathStateSlice = createSlice({
  name: 'processMetadata',
  initialState: processMetadataInitialState,
  reducers: {
    setSelectedTypeOfInvestment: (state: ProcessMetadata, action: PayloadAction<ProcessMetadataProductTypePayload>) => {
      return {...state, selectedTypeOfInvestment: action.payload.selectedTypeOfInvestment};
    },
  },
});

const sessionStoragePrefix = 'productWizardState';

const store = configureStore<ProductWizardState>({
  reducer: combineReducers<ProductWizardState>({
    config: configSlice.reducer,
    apiState: apiStateSlice.reducer,
    uiState: uiStateSlice.reducer,
    cartState: cartStateSlice.reducer,
    processMetadataState: chosenPathStateSlice.reducer,
  }),
  preloadedState: {
    uiState: loadStateFromStorage(sessionStorage, sessionStoragePrefix + '.uiState'),
    cartState: loadStateFromStorage(sessionStorage, sessionStoragePrefix + '.cartState'),
    processMetadataState: loadStateFromStorage(sessionStorage, sessionStoragePrefix + '.processMetadataState'),
  },
});

store.subscribe(() => {
  saveStateInStorage(sessionStorage, sessionStoragePrefix + '.uiState', store.getState().uiState);
  saveStateInStorage(sessionStorage, sessionStoragePrefix + '.cartState', store.getState().cartState);
  saveStateInStorage(sessionStorage, sessionStoragePrefix + '.processMetadataState', store.getState().processMetadataState);
});

export default store;

export const productWizardActions = { ...configSlice.actions, ...uiStateSlice.actions, ...apiStateSlice.actions, ...cartStateSlice.actions, ...chosenPathStateSlice.actions };
